// Kniha úrazů, seznam záznamů (15. 10. 2026, schválený mockup).
// Owner, manažer a CSM vidí všechny záznamy, trenér jen ty, které zapsal nebo kde vedl trénink.
// Na počítači tabulka, na telefonu řádky. Dlaždice nahoře fungují jako filtr.
import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight, Clock, FileText, Bandage, Trash2 } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, DlazdiceFiltr, EmptyState } from "../../components/ui.jsx";
import { Pil, RadekKontaktu, KartaLista } from "../../components/sales/salesUi.jsx";
import { apiFetch, useApiData } from "../../lib/api.js";
import { confirmDialog } from "../../components/confirmHost.jsx";
import { toast } from "../../components/Toast.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { STAV, zaklad, fmtDatum } from "./urazUi.jsx";

const FILTRY = [
  { key: "vse", popisek: "Letos záznamů", ton: "znacka" },
  { key: "ceka_podpis", popisek: "Čeká na podpis", ton: "pozor" },
  { key: "ceka_projednani", popisek: "Čeká na projednání", ton: "znacka" },
  { key: "pojistne", popisek: "Pojistné události", ton: "info" },
];

const trenink = (z) => {
  if (z.typ_treninku === "bootcamp") return [z.bootcamp_nazev || "Bootcamp", z.trener_jmeno].filter(Boolean).join(", ");
  if (z.typ_treninku === "osobni") return ["Osobní", z.trener_jmeno].filter(Boolean).join(", ");
  return z.typ_treninku_text || "";
};
// Červené tlačítko s košem, stejná velikost jako tlačítko se šipkou (btn-ikona: 44 px na telefonu).
const TL_SMAZAT = "btn-ikona shrink-0 border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50";
const zraneni = (z) => [z.zraneni_druh, [z.cast_tela, z.strana && z.strana !== "Neurčeno" ? z.strana.toLowerCase() : null].filter(Boolean).join(", ")].filter(Boolean).join(", ");

export default function KnihaUrazu() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const base = zaklad(pathname);
  const uzka = useUzkaObrazovka();
  const { data, loading, refetch } = useApiData("/api/uraz", { bezKese: true });
  const [filtr, setFiltr] = useState("vse");
  const [maze, setMaze] = useState(null);
  const smiSmazat = !!data?.prava?.smazat; // jen owner, server to hlídá znovu
  const zaznamy = data?.zaznamy || [];
  const p = data?.pocty || {};
  const rok = new Date().getFullYear();

  const vybrane = useMemo(() => {
    if (filtr === "ceka_podpis") return zaznamy.filter((z) => z.stav === "ceka_podpis" || z.stav === "koncept");
    if (filtr === "ceka_projednani") return zaznamy.filter((z) => z.stav === "ceka_projednani");
    if (filtr === "pojistne") return zaznamy.filter((z) => z.pojistna_podana);
    return zaznamy;
  }, [zaznamy, filtr]);
  const hodnoty = { vse: p.letos ?? "…", ceka_podpis: data ? (p.ceka_podpis || 0) + (p.koncept || 0) : "…", ceka_projednani: p.ceka_projednani ?? "…", pojistne: p.pojistne ?? "…" };
  // Smazání záznamu (3. 11. 2026, jen owner): potvrzení, pak smazání i s podpisy a soubory.
  async function smazat(z) {
    const ok = await confirmDialog({ title: `Smazat záznam o úrazu č. ${z.id}?`, description: "Smaže se i podpis a soubory, vrátit to nejde.", confirmLabel: "Smazat", cancelLabel: "Zrušit", variant: "danger" });
    if (!ok) return;
    setMaze(z.id);
    try {
      const r = await apiFetch("/api/uraz", { method: "POST", body: JSON.stringify({ akce: "smazat", id: z.id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
      toast(`Záznam č. ${z.id} je smazaný.`, "success");
      refetch();
    } catch (e) { toast(e.message, "error"); } finally { setMaze(null); }
  }
  const tlSmazat = (z) => smiSmazat ? (
    <button type="button" onClick={() => smazat(z)} disabled={maze === z.id} className={TL_SMAZAT} aria-label={`Smazat záznam ${z.id}`} title="Smazat záznam">
      <Trash2 className="h-5 w-5" />
    </button>
  ) : null;
  const stavPil = (z) => { const s = STAV[z.stav] || STAV.koncept; return <Pil ton={s.ton} casti={s.text} mala={!uzka} />; };

  return (
    <Stranka>
      <GradientHeader title="Kniha úrazů" icon={Bandage}
        akce={[{ klic: "novy", ikona: Plus, popis: "Zapsat úraz", onClick: () => navigate(`${base}/novy`) }]}
        napoveda={`Záznam o úrazu na tréninku, bootcampu nebo mimo trénink. Uchovává se 10 let. Vidí ho jen owner, manažer, CSM a trenér, který ho zapsal.`} />

      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {FILTRY.map((f) => (
          <DlazdiceFiltr key={f.key} popisek={f.key === "vse" ? `Záznamů ${rok}` : f.popisek} hodnota={hodnoty[f.key]} ton={f.ton}
            zapnuto={filtr === f.key} onClick={() => setFiltr(f.key)} />
        ))}
      </div>

      <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-[13px] font-semibold text-amber-700">
        <Clock className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Úraz se zapisuje do 24 hodin. Když ho trenér do té doby nedokončí, přijde jemu a manažerovi upozornění na Slack.</span>
      </div>

      <KartaLista ikona={FileText} nazev="Záznamy">
        {loading && !data ? <div className="h-16 animate-pulse rounded-xl bg-brand-light" />
          : !vybrane.length ? <EmptyState icon={Bandage} title="Žádný záznam" hint={filtr === "vse" ? "Zatím tu není žádný úraz." : "V tomhle výběru nic není."} />
            : uzka ? (
              <div className="-mx-4 -mb-4">
                {vybrane.map((z, i) => (
                  <RadekKontaktu key={z.id} prvni={i === 0} jmeno={<span className="demo-sensitive">{z.zraneny_jmeno || "bez jména"}</span>}
                    onOtevrit={() => navigate(`${base}/${z.id}`)} akce={tlSmazat(z)}
                    pilulky={<><Pil ton="neutralni" casti={[`č. ${z.id}`, fmtDatum(z.datum) || "bez data"]} />{stavPil(z)}</>}
                    poznamka={[trenink(z), zraneni(z)].filter(Boolean).join(", ") || null} />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2">Č.</th><th className="px-3 py-2">Datum</th><th className="px-3 py-2">Zraněný</th><th className="px-3 py-2">Místo</th>
                    <th className="px-3 py-2">Trénink</th><th className="px-3 py-2">Zranění</th><th className="px-3 py-2">Stav</th><th className="px-3 py-2" />
                  </tr></thead>
                  <tbody>
                    {vybrane.map((z) => (
                      <tr key={z.id} className="border-t border-slate-100">
                        <td className="px-3 py-2.5 font-bold tabular-nums text-slate-500">{z.id}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-slate-900">{fmtDatum(z.datum) || "bez data"}</td>
                        <td className="px-3 py-2.5"><div className="font-bold text-brand-deep demo-sensitive">{z.zraneny_jmeno || "bez jména"}</div>{z.zraneny_typ_text && <div className="mt-1"><Pil ton="neutralni" casti={z.zraneny_typ_text} mala /></div>}</td>
                        <td className="px-3 py-2.5 text-slate-700">{z.misto || ""}</td>
                        <td className="px-3 py-2.5 text-slate-700">{trenink(z)}</td>
                        <td className="px-3 py-2.5 text-slate-700">{zraneni(z)}</td>
                        <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1">{stavPil(z)}{z.pojistna_podana && <Pil ton="info" casti="pojistná událost" mala />}</div></td>
                        <td className="px-3 py-2.5 text-right"><div className="flex justify-end gap-2">{tlSmazat(z)}<Link to={`${base}/${z.id}`} className="btn-ikona" aria-label={`Otevřít záznam ${z.id}`} title="Otevřít"><ArrowUpRight className="h-5 w-5" /></Link></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </KartaLista>
    </Stranka>
  );
}
